import { KnownDeliveryState, KnownOrderDtoStatus } from '@groundfloor-platform/mgmt-service-g-sdk';
import { BookingFlowType } from '../../ui/enums/booking-flow-type.enum';
import { deliveredVerbForBookingFlowType } from '../../ui/util/delivered.verb';

/**
 * Maps order status labels based on booking flow type
 * For inventory flow: "delivered" -> "allocated", "delivered-offline" -> "allocated offline"
 */
export function getOrderStatusLabel(status: KnownOrderDtoStatus, bookingFlowType: BookingFlowType): string {
  // For inventory flow, replace "delivered" terms with "allocated"
  if (bookingFlowType === BookingFlowType.INVENTORY) {
    switch (status) {
      case 'delivered':
        return deliveredVerbForBookingFlowType(bookingFlowType);
      case 'delivered-offline':
        return `${deliveredVerbForBookingFlowType(bookingFlowType)} Offline`;
      default:
        return getDefaultOrderStatusLabel(status);
    }
  }

  return getDefaultOrderStatusLabel(status);
}

/**
 * Maps delivery state labels based on booking flow type
 * For inventory flow: "delivered" -> "allocated", "delivered-offline" -> "allocated offline"
 */
export function getDeliveryStateLabel(state: KnownDeliveryState, bookingFlowType: BookingFlowType): string {
  // For inventory flow, replace "delivered" terms with "allocated"
  if (bookingFlowType === BookingFlowType.INVENTORY) {
    switch (state) {
      case 'delivered':
        return deliveredVerbForBookingFlowType(bookingFlowType);
      case 'delivered-offline':
        return `${deliveredVerbForBookingFlowType(bookingFlowType)} Offline`;
      default:
        return getDefaultDeliveryStateLabel(state);
    }
  }

  return getDefaultDeliveryStateLabel(state);
}

/**
 * Default order status label mapping (unchanged from original)
 */
function getDefaultOrderStatusLabel(status: KnownOrderDtoStatus): string {
  const statusToLabel: Record<KnownOrderDtoStatus, string> = {
    booked: 'Booked',
    'in-progress': 'In Progress',
    delivered: 'Delivered',
    'delivered-offline': 'Delivered Offline',
    overdue: 'Overdue',
    collected: 'Collected',
    'retrieval-required': 'Retrieval Required',
    cancelled: 'Cancelled',
    retrieved: 'Retrieved'
  };

  return statusToLabel[status] || status;
}

/**
 * Default delivery state label mapping (unchanged from original)
 */
function getDefaultDeliveryStateLabel(state: KnownDeliveryState): string {
  const stateToLabel: Record<KnownDeliveryState, string> = {
    'in-progress': 'In Progress',
    delivered: 'Delivered',
    'delivered-offline': 'Delivered Offline',
    overdue: 'Overdue',
    collected: 'Collected',
    cancelled: 'Cancelled',
    retrieved: 'Retrieved',
    'retrieval-required': 'Retrieval Required',
    abandoned: 'Abandoned'
  };

  return stateToLabel[state] || state;
}

/**
 * Maps order status labels with category prefix based on booking flow type
 * For inventory flow: replaces "delivered" terms with "allocated"
 */
export function getOrderStatusLabelWithCategory(status: KnownOrderDtoStatus, bookingFlowType: BookingFlowType): string {
  // For inventory flow, replace "delivered" terms with "allocated"
  if (bookingFlowType === BookingFlowType.INVENTORY) {
    switch (status) {
      case 'delivered':
        return `- ${deliveredVerbForBookingFlowType(bookingFlowType)}`;
      case 'delivered-offline':
        return `- ${deliveredVerbForBookingFlowType(bookingFlowType)} Offline`;
      default:
        return getDefaultOrderStatusLabelWithCategory(status);
    }
  }

  return getDefaultOrderStatusLabelWithCategory(status);
}

/**
 * Default order status label with category mapping (unchanged from original)
 */
function getDefaultOrderStatusLabelWithCategory(status: KnownOrderDtoStatus): string {
  const statusToLabelWithCategory: Record<string, string> = {
    booked: '- Booked',
    delivered: '- Delivered',
    overdue: '- Overdue',
    collected: '- Collected',
    'retrieval-required': '- Retrieval Required',
    cancelled: '- Cancelled',
    retrieved: '- Retrieved',
    completed: 'Completed',
    'not-delivered': 'Not Delivered',
    'delivered-offline': '- Delivered Offline',
    'current-deliveries-category': 'Current'
  };

  return statusToLabelWithCategory[status] || status;
}

/**
 * Get order status label using the resolved booking flow type
 */
export function getOrderStatusLabelForProject(status: KnownOrderDtoStatus, bookingFlowType: BookingFlowType): string {
  return getOrderStatusLabel(status, bookingFlowType);
}

/**
 * Get delivery state label using the resolved booking flow type
 */
export function getDeliveryStateLabelForProject(state: KnownDeliveryState, bookingFlowType: BookingFlowType): string {
  return getDeliveryStateLabel(state, bookingFlowType);
}

/**
 * Get order status label with category using the resolved booking flow type
 */
export function getOrderStatusLabelWithCategoryForProject(
  status: KnownOrderDtoStatus,
  bookingFlowType: BookingFlowType
): string {
  return getOrderStatusLabelWithCategory(status, bookingFlowType);
}

/**
 * Transform timeline text content to replace "delivered" terms with "allocated" for inventory flow
 */
const allocationStatusMapping: Record<string, string> = {
  deliver: 'allocate',
  delivered: 'allocated',
  delivery: 'allocation',
  deliveries: 'allocations'
};

export function transformTimelineTextForProject(
  text: string | undefined,
  bookingFlowType: BookingFlowType
): string | undefined {
  if (!text) return text;

  if (bookingFlowType === BookingFlowType.INVENTORY) {
    return text.replace(/\b(deliver(?:ed|y|ies)?|deliver)\b/gi, (match) => {
      // Map base words to their allocation equivalents

      const replacement = allocationStatusMapping[match.toLowerCase()];

      if (!replacement) return match; // No mapping found, return original

      // Preserve case style
      if (match === match.toUpperCase()) {
        return replacement.toUpperCase(); // ALL CAPS
      } else if (match[0] === match[0].toUpperCase()) {
        return replacement[0].toUpperCase() + replacement.slice(1); // Capitalized
      } else {
        return replacement.toLowerCase(); // Lowercase
      }
    });
  }

  return text;
}
